// Componentes de interfaz compartidos. Pensados para usar con una mano y con guantes de garita:
// botones grandes, texto claro y estados con color + texto.

import { Component, createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router";
import { ESTADOS_PEDIDO, type EstadoPedido, type Tono } from "@porteria/core";
import { mensajeDeError } from "./firebase";
import { Icono, type NombreIcono } from "./iconos";

export function Pantalla(props: {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  volver?: string | true;
  /** Va a la izquierda del título: el logo del conjunto en los paneles de cada rol. */
  logo?: ReactNode;
  acciones?: ReactNode;
  children: ReactNode;
  pie?: ReactNode;
}) {
  const navegar = useNavigate();
  return (
    <div className="pantalla">
      <header className="encabezado">
        {props.volver && (
          <button className="boton-icono" aria-label="Volver" onClick={() => (props.volver === true ? navegar(-1) : navegar(props.volver as string))}>
            <Icono nombre="atras" />
          </button>
        )}
        {props.logo}
        <div className="encabezado-texto">
          <h1>{props.titulo}</h1>
          {props.subtitulo && <p>{props.subtitulo}</p>}
        </div>
        {props.acciones && <div className="encabezado-acciones">{props.acciones}</div>}
      </header>
      <main className="contenido">{props.children}</main>
      {props.pie && <nav className="pie">{props.pie}</nav>}
    </div>
  );
}

export function Pestana(props: { a: string; icono: NombreIcono; texto: string; activa: boolean; aviso?: number }) {
  return (
    <Link to={props.a} className={"pestana" + (props.activa ? " activa" : "")} aria-current={props.activa ? "page" : undefined}>
      <span className="pestana-icono" aria-hidden>
        <Icono nombre={props.icono} tamano={22} />
      </span>
      <span>{props.texto}</span>
      {!!props.aviso && <span className="globo">{props.aviso}</span>}
    </Link>
  );
}

export function Tarjeta(props: { children: ReactNode; className?: string; onClick?: () => void }) {
  return (
    <section className={"tarjeta " + (props.className ?? "")} onClick={props.onClick}>
      {props.children}
    </section>
  );
}

type Variante = "primario" | "secundario" | "peligro" | "texto" | "exito";

export function Boton({
  variante = "primario",
  grande,
  ocupado,
  children,
  className,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; grande?: boolean; ocupado?: boolean }) {
  return (
    <button
      className={`boton boton-${variante}${grande ? " boton-grande" : ""} ${className ?? ""}`}
      aria-busy={ocupado || undefined}
      {...resto}
      // Después de `resto`: mientras está ocupado no se puede tocar otra vez, aunque llegue disabled={false}.
      disabled={ocupado || resto.disabled}
    >
      {ocupado ? <span className="girando" aria-hidden /> : null}
      {children}
    </button>
  );
}

export function Etiqueta({ tono = "neutro", children }: { tono?: Tono; children: ReactNode }) {
  return <span className={`etiqueta tono-${tono}`}>{children}</span>;
}

export function EstadoDePedido({ estado }: { estado: EstadoPedido }) {
  const e = ESTADOS_PEDIDO[estado];
  return <Etiqueta tono={e.tono}>{e.etiqueta}</Etiqueta>;
}

/**
 * Campo con etiqueta, ayuda y error. Con `grupo` es un <div role="group"> y no un <label>: el navegador le pasa al
 * primer botón de un <label> cualquier toque en la etiqueta o en la ayuda, y con Opciones eso cambia la elección.
 */
export function Campo(props: { etiqueta: string; ayuda?: ReactNode; error?: string | null; grupo?: boolean; children: ReactNode }) {
  const id = useId();
  const pie = props.error ? <span className="campo-error">{props.error}</span> : props.ayuda ? <span className="campo-ayuda">{props.ayuda}</span> : null;
  if (props.grupo) {
    return (
      <div className="campo" role="group" aria-labelledby={id}>
        <span className="campo-etiqueta" id={id}>
          {props.etiqueta}
        </span>
        {props.children}
        {pie}
      </div>
    );
  }
  return (
    <label className="campo">
      <span className="campo-etiqueta">{props.etiqueta}</span>
      {props.children}
      {pie}
    </label>
  );
}

export function Opciones<T extends string>(props: { valor: T; opciones: { valor: T; texto: ReactNode }[]; alCambiar: (v: T) => void; nombre: string }) {
  return (
    <div className="opciones" role="radiogroup" aria-label={props.nombre}>
      {props.opciones.map((o) => (
        <button
          key={o.valor}
          type="button"
          role="radio"
          aria-checked={props.valor === o.valor}
          className={"opcion" + (props.valor === o.valor ? " elegida" : "")}
          onClick={() => props.alCambiar(o.valor)}
        >
          {o.texto}
        </button>
      ))}
    </div>
  );
}

export function Interruptor(props: { activo: boolean; alCambiar: (v: boolean) => void; texto: ReactNode; detalle?: ReactNode; ocupado?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.activo}
      className={"interruptor" + (props.activo ? " encendido" : "")}
      disabled={props.ocupado}
      onClick={() => props.alCambiar(!props.activo)}
    >
      <span className="interruptor-texto">
        <strong>{props.texto}</strong>
        {props.detalle && <small>{props.detalle}</small>}
      </span>
      <span className="interruptor-pista" aria-hidden>
        <span className="interruptor-bola" />
      </span>
    </button>
  );
}

export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return (
    <div className="cargando" role="status">
      <span className="girando" aria-hidden /> {texto}
    </div>
  );
}

export function Vacio(props: { icono: NombreIcono; titulo: string; children?: ReactNode }) {
  return (
    <div className="vacio">
      <div className="vacio-icono" aria-hidden>
        <Icono nombre={props.icono} tamano={30} />
      </div>
      <strong>{props.titulo}</strong>
      {props.children && <p>{props.children}</p>}
    </div>
  );
}

/** Logo del conjunto que sube la administración. Sin logo, las iniciales del nombre sobre el color de la marca. */
export function LogoConjunto(props: { nombre: string | null | undefined; url?: string | null; tamano?: "chico" | "grande" }) {
  const clase = "logo-conjunto" + (props.tamano ? ` ${props.tamano}` : "");
  if (props.url) {
    return (
      <span className={clase}>
        <img src={props.url} alt={props.nombre ? `Logo de ${props.nombre}` : "Logo del conjunto"} />
      </span>
    );
  }
  return (
    <span className={clase + " sin-logo"} aria-hidden>
      {iniciales(props.nombre ?? "")}
    </span>
  );
}

/** "Conjunto Residencial Los Robles" → "LR": se saltan las palabras genéricas y las cortas ("de", "la"). */
export function iniciales(nombre: string): string {
  const genericas = /^(conjunto|residencial|edificio|torres?|urbanizaci[oó]n|condominio|club|agrupaci[oó]n|unidad|ph|p\.h\.?)$/i;
  const palabras = nombre.split(/\s+/).filter((p) => p.length > 2 && !genericas.test(p));
  const base = palabras.length ? palabras : nombre.split(/\s+/).filter(Boolean);
  return base
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

/** Bloque de bienvenida arriba de cada panel, con el color de la marca. */
export function Portada(props: { titulo: ReactNode; children?: ReactNode; icono?: NombreIcono; tono?: "marca" | "tinta" }) {
  return (
    <section className={"portada" + (props.tono === "tinta" ? " tinta" : "")}>
      <div className="portada-titulo">{props.titulo}</div>
      {props.children}
      {props.icono && <Icono nombre={props.icono} tamano={84} className="portada-icono" />}
    </section>
  );
}

export function Fila(props: { children: ReactNode; className?: string }) {
  return <div className={"fila " + (props.className ?? "")}>{props.children}</div>;
}

/** Etiqueta y valor en una línea. `apilado`: la etiqueta encima del valor, para textos largos que en celular no caben lado a lado. */
export function Dato(props: { etiqueta: ReactNode; children: ReactNode; apilado?: boolean }) {
  return (
    <div className={props.apilado ? "dato apilado" : "dato"}>
      <span>{props.etiqueta}</span>
      <strong>{props.children}</strong>
    </div>
  );
}

/**
 * Panel que sube desde abajo (en pantallas grandes, un diálogo centrado). Se dibuja en <body>: así sus
 * botones nunca pertenecen al <form> de la pantalla que la abre (un toque no envía ese formulario).
 */
export function Hoja(props: { abierta: boolean; alCerrar: () => void; titulo: ReactNode; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (props.abierta && !d.open) d.showModal();
    if (!props.abierta && d.open) d.close();
  }, [props.abierta]);
  return createPortal(
    <dialog ref={ref} className="hoja" onClose={props.alCerrar} onCancel={props.alCerrar}>
      {props.abierta && (
        <div className="hoja-cuerpo">
          <div className="hoja-encabezado">
            <h2>{props.titulo}</h2>
            <button type="button" className="boton-icono" aria-label="Cerrar" onClick={props.alCerrar}>
              <Icono nombre="cerrar" />
            </button>
          </div>
          {props.children}
        </div>
      )}
    </dialog>,
    document.body,
  );
}

/**
 * Si una pantalla falla al dibujarse (p. ej. tras publicar una versión nueva el archivo viejo ya no existe),
 * muestra un aviso con botón de recargar en vez de dejar la app en blanco. `clave` (la ruta) lo reinicia.
 */
export class LimiteDeError extends Component<{ clave: string; children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("Pantalla con error", error);
  }

  componentDidUpdate(antes: { clave: string }) {
    if (antes.clave !== this.props.clave && this.state.error) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="pantalla">
        <main className="contenido">
          <Vacio icono="recargar" titulo="No pudimos abrir esta pantalla">
            Puede que haya una versión nueva de la app o que se haya ido la señal. Recarga para intentarlo de nuevo.
          </Vacio>
          <Boton grande type="button" onClick={() => location.reload()}>
            Recargar
          </Boton>
          <a className="boton boton-texto" href="/inicio?elegir">
            Ir al inicio
          </a>
        </main>
      </div>
    );
  }
}

// ——— Avisos (toasts) y preguntas ———

type TipoAviso = "ok" | "error" | "info";
interface Pregunta {
  titulo: string;
  texto?: ReactNode;
  confirmar: string;
  peligro?: boolean;
  /** Si se pide, la respuesta es el texto escrito. */
  campo?: { etiqueta: string; obligatorio?: boolean; ayuda?: string };
}

interface Avisos {
  avisar: (texto: string, tipo?: TipoAviso) => void;
  /** Devuelve el texto (o "" sin campo) si confirma, null si cancela. */
  preguntar: (p: Pregunta) => Promise<string | null>;
}

const ContextoAvisos = createContext<Avisos | null>(null);

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<{ id: number; texto: string; tipo: TipoAviso }[]>([]);
  const [pregunta, setPregunta] = useState<(Pregunta & { responder: (v: string | null) => void }) | null>(null);
  const [respuesta, setRespuesta] = useState("");

  const avisar = useCallback((texto: string, tipo: TipoAviso = "ok") => {
    const id = Date.now() + Math.random();
    setAvisos((a) => [...a, { id, texto, tipo }]);
    setTimeout(() => setAvisos((a) => a.filter((x) => x.id !== id)), tipo === "error" ? 6000 : 3500);
  }, []);

  const preguntar = useCallback(
    (p: Pregunta) =>
      new Promise<string | null>((resolver) => {
        setRespuesta("");
        setPregunta({ ...p, responder: resolver });
      }),
    [],
  );

  const cerrar = (v: string | null) => {
    pregunta?.responder(v);
    setPregunta(null);
  };

  return (
    <ContextoAvisos.Provider value={{ avisar, preguntar }}>
      {children}
      <div className="avisos" aria-live="polite">
        {avisos.map((a) => (
          <div key={a.id} className={`aviso aviso-${a.tipo}`} role={a.tipo === "error" ? "alert" : "status"}>
            {a.texto}
          </div>
        ))}
      </div>
      <Hoja abierta={!!pregunta} alCerrar={() => cerrar(null)} titulo={pregunta?.titulo ?? ""}>
        {pregunta && (
          <form
            className="pila"
            onSubmit={(e) => {
              e.preventDefault();
              if (pregunta.campo?.obligatorio && !respuesta.trim()) return;
              cerrar(pregunta.campo ? respuesta.trim() : "");
            }}
          >
            {pregunta.texto && <div className="texto-suave">{pregunta.texto}</div>}
            {pregunta.campo && (
              <Campo etiqueta={pregunta.campo.etiqueta} ayuda={pregunta.campo.ayuda}>
                <textarea autoFocus rows={3} value={respuesta} onChange={(e) => setRespuesta(e.target.value)} maxLength={300} />
              </Campo>
            )}
            <Fila>
              <Boton type="button" variante="secundario" onClick={() => cerrar(null)}>
                Cancelar
              </Boton>
              <Boton type="submit" variante={pregunta.peligro ? "peligro" : "primario"} disabled={!!pregunta.campo?.obligatorio && !respuesta.trim()}>
                {pregunta.confirmar}
              </Boton>
            </Fila>
          </form>
        )}
      </Hoja>
    </ContextoAvisos.Provider>
  );
}

export function useAvisos(): Avisos {
  const a = useContext(ContextoAvisos);
  if (!a) throw new Error("useAvisos fuera de ProveedorAvisos");
  return a;
}

/** Ejecuta una acción mostrando "ocupado" y el error en un aviso. */
export function useAccion() {
  const { avisar } = useAvisos();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const ejecutar = useCallback(
    async <T,>(clave: string, accion: () => Promise<T>, exito?: string): Promise<T | undefined> => {
      setOcupado(clave);
      try {
        const r = await accion();
        if (exito) avisar(exito, "ok");
        return r;
      } catch (e) {
        avisar(mensajeDeError(e), "error");
        return undefined;
      } finally {
        setOcupado(null);
      }
    },
    [avisar],
  );
  return { ejecutar, ocupado };
}
